import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, Save, Loader2, Plus, Trash2, ChevronDown, ChevronUp, Sparkles,
  ListChecks, BookOpen, Settings, AlertCircle, CheckCircle2, RefreshCw,
  Award, Eye,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function adminHeaders() {
  const tok = localStorage.getItem("admin_token") || sessionStorage.getItem("admin_token");
  return tok ? { Authorization: `Bearer ${tok}` } : {};
}

export default function AdminCourseEditorPage() {
  const { course_id } = useParams();
  const navigate = useNavigate();
  const isNew = course_id === "new";
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(!isNew);
  const [tab, setTab] = useState("settings"); // settings | modules | exam
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  async function load() {
    if (isNew) {
      setCourse({
        slug: "", title: "", description: "", summary: "",
        cover_image_url: "", price_inr: 1999, original_price_inr: 2999,
        target_audience: "", prerequisites: [], learning_objectives: [],
        cert_role: "", cert_body_description: "",
        preview_video_url: "",
        modules: [], final_exam: null,
      });
      return;
    }
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/courses/${course_id}`, { headers: adminHeaders() });
      setCourse(r.data);
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Could not load course");
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, [course_id]);

  async function saveDetails() {
    setSaving(true); setMsg("");
    try {
      if (isNew) {
        const r = await axios.post(`${API}/admin/courses`, {
          slug: course.slug,
          title: course.title,
          description: course.description,
          summary: course.summary,
          cover_image_url: course.cover_image_url,
          price_inr: parseInt(course.price_inr) || 1999,
          original_price_inr: parseInt(course.original_price_inr) || null,
          target_audience: course.target_audience,
          prerequisites: course.prerequisites,
          learning_objectives: course.learning_objectives,
          category: course.category,
          tags: course.tags || [],
        }, { headers: adminHeaders() });
        navigate(`/admin/shanso-learn/course/${r.data.course_id}`);
        return;
      }
      const payload = {
        title: course.title,
        description: course.description,
        summary: course.summary,
        cover_image_url: course.cover_image_url,
        price_inr: parseInt(course.price_inr) || 1999,
        original_price_inr: parseInt(course.original_price_inr) || null,
        target_audience: course.target_audience,
        prerequisites: course.prerequisites,
        learning_objectives: course.learning_objectives,
        cert_role: course.cert_role,
        cert_body_description: course.cert_body_description,
        preview_video_url: course.preview_video_url,
        category: course.category,
        tags: course.tags || [],
      };
      await axios.put(`${API}/admin/courses/${course_id}`, payload, { headers: adminHeaders() });
      setMsg("✓ Saved");
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  }

  if (loading || !course) return <div className="min-h-screen bg-[#0a0a0a] text-zinc-400 flex items-center justify-center"><Loader2 className="animate-spin" /></div>;

  return (
    <div className="text-white">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5 pb-4 border-b border-zinc-800/60">
        <div className="flex items-center gap-2">
          <Link to="/admin/shanso-learn" className="text-zinc-400 hover:text-white" data-testid="back-to-shansolearn"><ArrowLeft size={16} /></Link>
          <p className="text-white text-xl font-bold truncate">{isNew ? "New course" : course.title}</p>
        </div>
        <nav className="flex items-center gap-1 text-[12px]">
          {[
            ["settings", "Settings", Settings],
            ["modules", `Modules (${(course.modules || []).length})`, BookOpen, isNew],
            ["exam", "Final exam", ListChecks, isNew],
          ].map(([k, label, Icon, disabled]) => (
            <button
              key={k}
              disabled={disabled}
              onClick={() => !disabled && setTab(k)}
              className={`px-3 py-1.5 rounded-lg inline-flex items-center gap-1 transition-colors ${tab === k ? "bg-cyan-500/10 text-cyan-300 border border-cyan-500/40" : "text-zinc-400 hover:text-white border border-transparent disabled:opacity-40 disabled:cursor-not-allowed"}`}
              data-testid={`editor-tab-${k}`}
            >
              <Icon size={12} /> {label}
            </button>
          ))}
        </nav>
      </div>

      <div className="space-y-4">
        {msg && (
          <div className={`text-xs p-2.5 rounded-lg ${msg.startsWith("✓") ? "bg-emerald-500/10 text-emerald-300 border border-emerald-500/30" : "bg-amber-500/10 text-amber-300 border border-amber-500/30"}`}>{msg}</div>
        )}
        {tab === "settings" && <SettingsTab course={course} setCourse={setCourse} isNew={isNew} saving={saving} onSave={saveDetails} />}
        {tab === "modules" && !isNew && <ModulesTab course={course} courseId={course_id} onReload={load} />}
        {tab === "exam" && !isNew && <ExamTab courseId={course_id} />}
      </div>
    </div>
  );
}

// ── Settings ─────────────────────────────────────────────────────────
function SettingsTab({ course, setCourse, isNew, saving, onSave }) {
  function set(k, v) { setCourse((c) => ({ ...c, [k]: v })); }
  function setList(k, txt) { setCourse((c) => ({ ...c, [k]: txt.split("\n").map((s) => s.trim()).filter(Boolean) })); }

  return (
    <div className="space-y-4">
      <Field label="Slug (URL-friendly, unique)" req>
        <input value={course.slug || ""} onChange={(e) => set("slug", e.target.value)} disabled={!isNew} className="input" data-testid="course-slug" placeholder="prompt-engineering" />
      </Field>
      <Field label="Title" req>
        <input value={course.title || ""} onChange={(e) => set("title", e.target.value)} className="input" data-testid="course-title" />
      </Field>
      <Field label="Summary (1-2 sentences for catalog cards)">
        <textarea rows={2} value={course.summary || ""} onChange={(e) => set("summary", e.target.value)} className="input" data-testid="course-summary" />
      </Field>
      <Field label="Description (long form for the course landing)">
        <textarea rows={3} value={course.description || ""} onChange={(e) => set("description", e.target.value)} className="input" data-testid="course-description" />
      </Field>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Price (INR)">
          <input type="number" value={course.price_inr || 1999} onChange={(e) => set("price_inr", e.target.value)} className="input" data-testid="course-price" />
        </Field>
        <Field label="Original price (strike-through, optional)">
          <input type="number" value={course.original_price_inr || ""} onChange={(e) => set("original_price_inr", e.target.value)} className="input" data-testid="course-orig-price" />
        </Field>
      </div>
      <Field label="Cover image URL">
        <input value={course.cover_image_url || ""} onChange={(e) => set("cover_image_url", e.target.value)} className="input" data-testid="course-cover" />
      </Field>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Primary category (used for the catalog filter)">
          <select value={course.category || ""} onChange={(e) => set("category", e.target.value)} className="input" data-testid="course-category">
            <option value="">— Select —</option>
            <option value="ai">AI & LLMs</option>
            <option value="career">Career</option>
            <option value="coding">Coding</option>
            <option value="productivity">Productivity</option>
          </select>
        </Field>
        <Field label="Tags (comma-separated, used for catalog search)">
          <input
            value={(course.tags || []).join(", ")}
            onChange={(e) => set("tags", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
            className="input"
            data-testid="course-tags"
            placeholder="ai, llm, prompt-engineering"
          />
        </Field>
      </div>
      <Field label="Free preview video URL (public, no auth)">
        <input value={course.preview_video_url || ""} onChange={(e) => set("preview_video_url", e.target.value)} className="input" placeholder="/shansolearn-preview/module_01_v2.mp4" />
      </Field>
      <Field label="Target audience">
        <input value={course.target_audience || ""} onChange={(e) => set("target_audience", e.target.value)} className="input" />
      </Field>
      <Field label="Prerequisites (one per line)">
        <textarea rows={3} value={(course.prerequisites || []).join("\n")} onChange={(e) => setList("prerequisites", e.target.value)} className="input" />
      </Field>
      <Field label="Learning objectives (one per line)">
        <textarea rows={3} value={(course.learning_objectives || []).join("\n")} onChange={(e) => setList("learning_objectives", e.target.value)} className="input" />
      </Field>

      <div className="border-t border-zinc-800 pt-4 mt-4">
        <p className="text-cyan-300 text-[11px] uppercase tracking-widest font-bold mb-2">Certificate</p>
        <p className="text-zinc-400 text-[11px] mb-3">Leave both fields blank to let our AI auto-derive them from the course's title, summary, objectives, and module list on first cert issuance — or set them manually to override.</p>
        <Field label="Role shown inside the hexagonal badge (e.g. 'Prompt Engineer', 'Python Developer')">
          <input value={course.cert_role || ""} onChange={(e) => set("cert_role", e.target.value)} className="input" data-testid="course-cert-role" placeholder="Auto-derived if blank" />
        </Field>
        <Field label="Body description on certificate (will read: 'has successfully completed [course title] and …')">
          <textarea rows={4} value={course.cert_body_description || ""} onChange={(e) => set("cert_body_description", e.target.value)} className="input" data-testid="course-cert-body" placeholder="Auto-derived from learning objectives if blank" />
        </Field>
        {!isNew && (
          <CertActions course={course} setCourse={setCourse} />
        )}
      </div>

      <div className="flex items-center gap-2 pt-2">
        <button onClick={onSave} disabled={saving} className="inline-flex items-center gap-1 px-4 py-2 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-black font-bold text-sm disabled:opacity-60" data-testid="save-course-btn">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save
        </button>
      </div>

      <style>{`.input { width: 100%; background: #18181b; border: 1px solid #27272a; border-radius: 8px; padding: 8px 12px; font-size: 13px; color: white; outline: none; }
                .input:focus { border-color: rgba(34,211,238,0.5); }`}</style>
    </div>
  );
}

function Field({ label, req, children }) {
  return (
    <div>
      <label className="text-zinc-400 text-[11px] uppercase tracking-wider font-bold block mb-1">{label}{req && <span className="text-red-400 ml-1">*</span>}</label>
      {children}
    </div>
  );
}

// ── Modules editor ───────────────────────────────────────────────────
function ModulesTab({ course, courseId, onReload }) {
  const [modules, setModules] = useState(course.modules || []);
  const [openMid, setOpenMid] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  function setMod(i, patch) {
    setModules((arr) => arr.map((m, idx) => idx === i ? { ...m, ...patch } : m));
  }
  function addModule() {
    setModules((arr) => [...arr, {
      module_no: arr.length + 1, title: "New module", summary: "",
      duration_min: 15, video_url: "", video_storage_path: "", quiz: [],
    }]);
  }
  function removeModule(i) {
    if (!window.confirm("Remove this module?")) return;
    setModules((arr) => arr.filter((_, idx) => idx !== i).map((m, idx) => ({ ...m, module_no: idx + 1 })));
  }

  async function saveAll() {
    setSaving(true); setMsg("");
    try {
      await axios.put(`${API}/admin/courses/${courseId}/modules`, modules, { headers: adminHeaders() });
      setMsg("✓ Modules saved");
      onReload();
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  }

  return (
    <div className="space-y-3" data-testid="modules-tab">
      {msg && <div className={`text-xs p-2.5 rounded-lg ${msg.startsWith("✓") ? "bg-emerald-500/10 text-emerald-300 border border-emerald-500/30" : "bg-amber-500/10 text-amber-300 border border-amber-500/30"}`}>{msg}</div>}
      <div className="flex items-center justify-between">
        <p className="text-zinc-300 text-sm">{modules.length} modules</p>
        <div className="flex items-center gap-2">
          <button onClick={addModule} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-zinc-300 hover:bg-zinc-800 text-sm border border-zinc-800"><Plus size={13} /> Add module</button>
          <button onClick={saveAll} disabled={saving} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-black font-bold text-sm disabled:opacity-60" data-testid="save-modules-btn">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save all
          </button>
        </div>
      </div>
      {modules.map((m, i) => {
        const open = openMid === i;
        return (
          <div key={i} className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl">
            <button onClick={() => setOpenMid(open ? null : i)} className="w-full flex items-center justify-between p-3 text-left">
              <div className="flex items-center gap-3">
                <span className="text-cyan-300 font-bold text-sm">M{m.module_no || (i + 1)}</span>
                <span className="text-white text-sm font-semibold">{m.title || "Untitled"}</span>
                <span className="text-zinc-500 text-[11px]">({m.duration_min || 0} min · {(m.quiz || []).length} quiz Qs)</span>
              </div>
              {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
            {open && (
              <div className="p-4 border-t border-zinc-800/60 space-y-3">
                <div className="grid sm:grid-cols-3 gap-3">
                  <Field label="Module #">
                    <input type="number" value={m.module_no || ""} onChange={(e) => setMod(i, { module_no: parseInt(e.target.value) || i + 1 })} className="input" />
                  </Field>
                  <Field label="Duration (min)">
                    <input type="number" value={m.duration_min || ""} onChange={(e) => setMod(i, { duration_min: parseInt(e.target.value) || 0 })} className="input" />
                  </Field>
                  <Field label="Module ID">
                    <input value={m.module_id || "(new)"} disabled className="input opacity-60" />
                  </Field>
                </div>
                <Field label="Title">
                  <input value={m.title || ""} onChange={(e) => setMod(i, { title: e.target.value })} className="input" />
                </Field>
                <Field label="Summary">
                  <textarea rows={2} value={m.summary || ""} onChange={(e) => setMod(i, { summary: e.target.value })} className="input" />
                </Field>
                <Field label="Video URL (public preview or YouTube embed) — leave empty if using storage path">
                  <input value={m.video_url || ""} onChange={(e) => setMod(i, { video_url: e.target.value })} className="input" />
                </Field>
                <Field label="Video storage path (object storage key)">
                  <input value={m.video_storage_path || ""} onChange={(e) => setMod(i, { video_storage_path: e.target.value })} className="input" placeholder="shansolearn/prompt-engineering/module_02.mp4" />
                </Field>

                <QuizEditor mod={m} onChange={(quiz) => setMod(i, { quiz })} moduleNo={m.module_no} />

                <div className="flex justify-between pt-2 border-t border-zinc-800">
                  <button onClick={() => removeModule(i)} className="text-red-300 hover:text-red-200 text-xs inline-flex items-center gap-1"><Trash2 size={12} /> Remove module</button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function QuizEditor({ mod, onChange, moduleNo }) {
  const [generating, setGenerating] = useState(false);

  function setQ(i, patch) { onChange(mod.quiz.map((q, idx) => idx === i ? { ...q, ...patch } : q)); }
  function setOpt(qi, oi, v) {
    const opts = [...mod.quiz[qi].options];
    opts[oi] = v;
    setQ(qi, { options: opts });
  }
  function addQuestion() {
    onChange([...(mod.quiz || []), { q: "", options: ["", "", "", ""], correct_index: 0, explanation: "" }]);
  }
  function removeQ(i) { onChange(mod.quiz.filter((_, idx) => idx !== i)); }

  async function regenerate() {
    if (!window.confirm("Replace this module's quiz with AI-generated questions?")) return;
    setGenerating(true);
    try {
      const r = await axios.post(`${API}/admin/courses/quiz/generate`, {
        module_topic: mod.title,
        learning_summary: mod.summary || mod.title,
        count: 5,
      }, { headers: adminHeaders() });
      onChange(r.data.questions || []);
    } catch (e) { alert(e?.response?.data?.detail || "Generate failed"); }
    finally { setGenerating(false); }
  }

  return (
    <div className="border border-zinc-800 rounded-lg p-3 bg-zinc-950/40">
      <div className="flex items-center justify-between mb-2">
        <p className="text-cyan-300 text-[11px] uppercase tracking-widest font-bold inline-flex items-center gap-1"><ListChecks size={12} /> Module quiz ({(mod.quiz || []).length} questions)</p>
        <div className="flex items-center gap-1">
          <button onClick={regenerate} disabled={generating} className="text-[11px] text-zinc-300 hover:bg-zinc-800 px-2 py-1 rounded inline-flex items-center gap-1 disabled:opacity-60">
            {generating ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />} Regen with AI
          </button>
          <button onClick={addQuestion} className="text-[11px] text-zinc-300 hover:bg-zinc-800 px-2 py-1 rounded inline-flex items-center gap-1"><Plus size={11} /> Add</button>
        </div>
      </div>
      {(mod.quiz || []).map((q, qi) => (
        <div key={qi} className="bg-zinc-900/60 border border-zinc-800 rounded-lg p-3 mb-2 space-y-2">
          <div className="flex items-start gap-2">
            <span className="text-zinc-500 text-xs mt-2">Q{qi + 1}.</span>
            <textarea rows={2} value={q.q || ""} onChange={(e) => setQ(qi, { q: e.target.value })} className="input flex-1" placeholder="Question text" />
            <button onClick={() => removeQ(qi)} className="text-red-300 hover:text-red-200 p-1"><Trash2 size={12} /></button>
          </div>
          {(q.options || []).map((opt, oi) => (
            <label key={oi} className="flex items-center gap-2">
              <input type="radio" checked={q.correct_index === oi} onChange={() => setQ(qi, { correct_index: oi })} className="accent-cyan-500" />
              <input value={opt} onChange={(e) => setOpt(qi, oi, e.target.value)} className="input flex-1" placeholder={`Option ${"ABCD"[oi]}`} />
            </label>
          ))}
          <input value={q.explanation || ""} onChange={(e) => setQ(qi, { explanation: e.target.value })} className="input" placeholder="Brief explanation shown after submit" />
        </div>
      ))}
    </div>
  );
}

// ── Final exam editor ────────────────────────────────────────────────
function ExamTab({ courseId }) {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [generating, setGenerating] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/courses/${courseId}`, { headers: adminHeaders() });
      setQuestions(r.data?.final_exam?.questions || []);
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, [courseId]);

  function setQ(i, patch) { setQuestions((arr) => arr.map((q, idx) => idx === i ? { ...q, ...patch } : q)); }
  function setOpt(qi, oi, v) {
    setQuestions((arr) => arr.map((q, idx) => idx === qi ? { ...q, options: q.options.map((o, j) => j === oi ? v : o) } : q));
  }
  function addQ() { setQuestions((arr) => [...arr, { q: "", options: ["", "", "", ""], correct_index: 0, explanation: "" }]); }
  function removeQ(i) { setQuestions((arr) => arr.filter((_, idx) => idx !== i)); }

  async function save() {
    if (questions.length < 5) { setMsg("Need at least 5 questions"); return; }
    setSaving(true); setMsg("");
    try {
      await axios.post(`${API}/admin/courses/${courseId}/exam`, { questions }, { headers: adminHeaders() });
      setMsg("✓ Saved");
    } catch (e) { setMsg(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  }

  async function regenerateExam() {
    const count = parseInt(prompt("How many questions to generate? (10-30 recommended)", "30") || "30");
    if (!count || count < 10) return;
    if (!window.confirm(`Replace exam with ${count} AI-generated questions?`)) return;
    setGenerating(true);
    try {
      // Get course modules summary
      const cr = await axios.get(`${API}/admin/courses/${courseId}`, { headers: adminHeaders() });
      const mods = cr.data.modules || [];
      const summary = mods.map((m) => `M${m.module_no}: ${m.title} — ${m.summary || ""}`).join("\n");
      const r = await axios.post(`${API}/admin/courses/exam/generate`, {
        course_title: cr.data.title, modules_summary: summary, count,
      }, { headers: adminHeaders(), timeout: 180000 });
      setQuestions(r.data.questions || []);
    } catch (e) { alert(e?.response?.data?.detail || "Generate failed"); }
    finally { setGenerating(false); }
  }

  if (loading) return <div className="text-zinc-400 text-sm"><Loader2 size={14} className="animate-spin inline" /> Loading…</div>;

  return (
    <div className="space-y-3" data-testid="exam-tab">
      {msg && <div className={`text-xs p-2.5 rounded-lg ${msg.startsWith("✓") ? "bg-emerald-500/10 text-emerald-300 border border-emerald-500/30" : "bg-amber-500/10 text-amber-300 border border-amber-500/30"}`}>{msg}</div>}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-zinc-300 text-sm">{questions.length} questions · 80% to pass · 45-min timer · 3 attempts</p>
        <div className="flex items-center gap-2">
          <button onClick={regenerateExam} disabled={generating} className="text-sm text-zinc-300 hover:bg-zinc-800 border border-zinc-800 px-3 py-1.5 rounded-lg inline-flex items-center gap-1 disabled:opacity-60">
            {generating ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} Regenerate with AI
          </button>
          <button onClick={addQ} className="text-sm text-zinc-300 hover:bg-zinc-800 border border-zinc-800 px-3 py-1.5 rounded-lg inline-flex items-center gap-1"><Plus size={13} /> Add</button>
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-black font-bold text-sm disabled:opacity-60" data-testid="save-exam-btn">
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save
          </button>
        </div>
      </div>
      {questions.map((q, qi) => (
        <div key={qi} className="bg-zinc-900/60 border border-zinc-800 rounded-lg p-3 space-y-2" data-testid={`exam-q-${qi}`}>
          <div className="flex items-start gap-2">
            <span className="text-zinc-500 text-xs mt-2">Q{qi + 1}.</span>
            <textarea rows={2} value={q.q || ""} onChange={(e) => setQ(qi, { q: e.target.value })} className="input flex-1" placeholder="Question" />
            <button onClick={() => removeQ(qi)} className="text-red-300 hover:text-red-200 p-1"><Trash2 size={12} /></button>
          </div>
          {(q.options || []).map((opt, oi) => (
            <label key={oi} className="flex items-center gap-2">
              <input type="radio" checked={q.correct_index === oi} onChange={() => setQ(qi, { correct_index: oi })} className="accent-cyan-500" />
              <input value={opt} onChange={(e) => setOpt(qi, oi, e.target.value)} className="input flex-1" placeholder={`Option ${"ABCD"[oi]}`} />
            </label>
          ))}
          <input value={q.explanation || ""} onChange={(e) => setQ(qi, { explanation: e.target.value })} className="input" placeholder="Brief explanation" />
        </div>
      ))}
      <style>{`.input { width: 100%; background: #18181b; border: 1px solid #27272a; border-radius: 6px; padding: 6px 10px; font-size: 12px; color: white; outline: none; }
                .input:focus { border-color: rgba(34,211,238,0.5); }`}</style>
    </div>
  );
}



// ── Certificate actions: Preview + AI Regenerate ──────────────────────
function CertActions({ course, setCourse }) {
  const [previewing, setPreviewing] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [msg, setMsg] = useState("");

  async function preview() {
    setMsg(""); setPreviewing(true);
    try {
      const res = await axios.post(
        `${API}/admin/courses/${course.course_id}/cert/preview`,
        {},
        { headers: adminHeaders(), responseType: "blob" },
      );
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank", "noopener");
      // Revoke after a short delay so the new tab has time to load
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      setMsg("✗ " + (e?.response?.data?.detail || "Could not generate preview"));
    } finally {
      setPreviewing(false);
    }
  }

  async function regenerate() {
    if (!window.confirm("Regenerate certificate role and body via AI? This will overwrite the current values."))
      return;
    setMsg(""); setRegenerating(true);
    try {
      const res = await axios.post(
        `${API}/admin/courses/${course.course_id}/cert/regenerate`,
        {},
        { headers: adminHeaders() },
      );
      setCourse((c) => ({
        ...c,
        cert_role: res.data.cert_role || "",
        cert_body_description: res.data.cert_body_description || "",
      }));
      setMsg("✓ Regenerated via AI. Click Save to persist.");
    } catch (e) {
      setMsg("✗ " + (e?.response?.data?.detail || "Regeneration failed"));
    } finally {
      setRegenerating(false);
    }
  }

  return (
    <div className="flex flex-wrap gap-2 mt-3">
      <button
        onClick={preview}
        disabled={previewing}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-cyan-500/40 text-cyan-200 hover:bg-cyan-500/10 text-xs font-semibold disabled:opacity-60"
        data-testid="preview-cert-btn"
      >
        {previewing ? <Loader2 size={12} className="animate-spin" /> : <Eye size={12} />} Preview certificate
      </button>
      <button
        onClick={regenerate}
        disabled={regenerating}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-zinc-700 text-zinc-300 hover:bg-zinc-800 text-xs font-semibold disabled:opacity-60"
        data-testid="regen-cert-meta-btn"
      >
        {regenerating ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} Regenerate via AI
      </button>
      {msg && (
        <span className={`text-[11px] inline-flex items-center gap-1 ml-1 ${msg.startsWith("✓") ? "text-emerald-300" : "text-amber-300"}`}>
          {msg}
        </span>
      )}
    </div>
  );
}
